import React, {
  Fragment,
  useCallback,
  useEffect,
  useReducer,
} from "react";
import { toast } from "react-toastify";
import ReactModal from "react-modal";
import { useAuth0 } from "@auth0/auth0-react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

import ai from "../../helpers/axios";
import TextInput from "../text-input";
import Loader from "../loader";
import { LoadingButton } from "../shared/loading-button";

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
      };
    }

    case "fieldChanged": {
      if (action.field === "selectedItem.name") {
        return {
          ...state,
          selectedItem: {
            ...(state.selectedItem || {}),
            name: action.value,
          },
        };
      }

      if (action.field === "selectedSubItem.name") {
        return {
          ...state,
          selectedSubItem: {
            ...(state.selectedSubItem || {}),
            name: action.value,
          },
        };
      }

      return {
        ...state,
        [action.field]: action.value,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  isLoading: true,
  expandedRows: [],
  data: [],
  totalLength: 0,
  isSaving: false,
  isAddNewItemMode: false,
  newCategoryName: "",
  newSubCategoryName: "",
  selectedItem: null,
  selectedSubItem: null,
  isDeleteModalOpen: false,
};

const CateringCatalog = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getData = useCallback(
    async () => {
      dispatch({ type: "dataBeganDownloading" });

      const token = await getAccessTokenSilently().catch(loginWithRedirect);

      const { data } = await ai.auth(token).get(`/api/catering/categories`, {});

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    },
    [getAccessTokenSilently, loginWithRedirect],
  );

  useEffect(() => {
    getData();
  }, [getData]);

  const handleToggleExpandAllRows = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "expandedRows",
      value: state.expandedRows.length > 0 ? [] : state.data.filter(({ subCategories }) => subCategories?.length > 0).map(({ _id }) => _id),
    });
  }, [state.expandedRows, state.data]);

  const handleToggleExpandRow = useCallback((id) => {
    dispatch({
      type: "fieldChanged",
      field: "expandedRows",
      value: state.expandedRows.includes(id) ? state.expandedRows.filter(row => row !== id) : [...state.expandedRows, id],
    });
  }, [state.expandedRows]);

  const handleAddNewRow = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "isAddNewItemMode",
      value: true,
    });
  }, []);

  const handleAddNewItem = useCallback(async () => {
    if (!state.newCategoryName) {
      toast.error(
        "Please fill in all required fields and retry.",
      );
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai.auth(token).post(`/api/catering/categories`, {
        name: state.newCategoryName,
      });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "isAddNewItemMode",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "newCategoryName",
        value: "",
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [state.newCategoryName, getAccessTokenSilently, loginWithRedirect, getData]);

  const handleHideNewRow = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "isAddNewItemMode",
      value: false,
    });
    dispatch({
      type: "fieldChanged",
      field: "newCategoryName",
      value: "",
    });
  }, []);

  const handleMoveItemUp = useCallback(async (item) => {
    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .put(`/api/catering/categories/${item._id}`, { name: item.name, order: item.order + 1 });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect, getData]);

  const handleMoveItemDown = useCallback(async (item) => {
    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .put(`/api/catering/categories/${item._id}`, { name: item.name, order: item.order - 1 });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect, getData]);

  const handleEditItem = useCallback((item) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: item,
    });
  }, []);

  const handleEditItemConfirm = useCallback(async () => {
    if (!state.selectedItem) {
      return;
    }

    if (!state.selectedItem.name) {
      toast.error(
        "Please fill in all required fields and retry.",
      );
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .put(`/api/catering/categories/${state.selectedItem._id}`, { name: state.selectedItem.name });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedItem",
        value: null,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [state.selectedItem, getAccessTokenSilently, loginWithRedirect, getData]);

  const handleCancelEditItem = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: null,
    });
  }, []);

  const handleDeleteItem = useCallback((item) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: item,
    });
    dispatch({
      type: "fieldChanged",
      field: "isDeleteModalOpen",
      value: true,
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!state.selectedItem) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .delete(`/api/catering/categories/${state.selectedItem._id}`);
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "isDeleteModalOpen",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedItem",
        value: null,
      });
      dispatch({
        type: "fieldChanged",
        field: "expandedRows",
        value: state.expandedRows.filter(row => row !== state.selectedItem._id),
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [state.selectedItem, state.expandedRows, getAccessTokenSilently, loginWithRedirect, getData]);

  const handleAddSubItem = useCallback((item) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: item,
    });
    dispatch({
      type: "fieldChanged",
      field: "isAddNewItemMode",
      value: true,
    });
  }, []);

  const handleAddNewSubItem = useCallback(async () => {
    if (!state.selectedItem) {
      return;
    }

    if (!state.newSubCategoryName) {
      toast.error(
        "Please fill in all required fields and retry.",
      );
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai.auth(token).post(`/api/catering/categories/${state.selectedItem._id}/subCategories`, {
        name: state.newSubCategoryName,
      });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "isAddNewItemMode",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "newSubCategoryName",
        value: "",
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedItem",
        value: null,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [state.selectedItem, state.newSubCategoryName, getAccessTokenSilently, loginWithRedirect, getData]);

  const handleHideNewSubRow = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: null,
    });
    dispatch({
      type: "fieldChanged",
      field: "isAddNewItemMode",
      value: false,
    });
  }, []);

  const handleMoveSubItemUp = useCallback(async (item) => {
    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .put(`/api/catering/categories/${item.categoryId}/subCategories/${item._id}`, {
          name: item.name,
          order: item.order + 1,
        });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect, getData]);

  const handleMoveSubItemDown = useCallback(async (item) => {
    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .put(`/api/catering/categories/${item.categoryId}/subCategories/${item._id}`, {
          name: item.name,
          order: item.order - 1,
        });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect, getData]);

  const handleEditSubItem = useCallback((item) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedSubItem",
      value: item,
    });
  }, []);

  const handleEditSubItemConfirm = useCallback(async () => {
    if (!state.selectedSubItem) {
      return;
    }

    if (!state.selectedSubItem.name) {
      toast.error(
        "Please fill in all required fields and retry.",
      );
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .put(`/api/catering/categories/${state.selectedSubItem.categoryId}/subCategories/${state.selectedSubItem._id}`, { name: state.selectedSubItem.name });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedSubItem",
        value: null,
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedItem",
        value: null,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [state.selectedSubItem, getAccessTokenSilently, loginWithRedirect, getData]);

  const handleCancelEditSubItem = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "selectedSubItem",
      value: null,
    });
  }, []);

  const handleDeleteSubItem = useCallback((item) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedSubItem",
      value: item,
    });
    dispatch({
      type: "fieldChanged",
      field: "isDeleteModalOpen",
      value: true,
    });
  }, []);

  const handleDeleteSubConfirm = useCallback(async () => {
    if (!state.selectedSubItem) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .delete(`/api/catering/categories/${state.selectedSubItem.categoryId}/subCategories/${state.selectedSubItem._id}`);
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "isDeleteModalOpen",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedItem",
        value: null,
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedSubItem",
        value: null,
      });
      const category = state.data.find(category => category._id === state.selectedSubItem.categoryId);
      if (category.subCategories?.filter(subCategory => subCategory._id !== state.selectedSubItem._id).length === 0) {
        dispatch({
          type: "fieldChanged",
          field: "expandedRows",
          value: state.expandedRows.filter(row => row !== state.selectedSubItem.categoryId),
        });
      }
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [state.selectedSubItem, state.expandedRows, state.data, getAccessTokenSilently, loginWithRedirect, getData]);

  const handleCloseDeleteModal = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: null,
    });
    dispatch({
      type: "fieldChanged",
      field: "isDeleteModalOpen",
      value: false,
    });
  }, []);

  return (
    <Fragment>
      <h5 className="mt-30 mb-1 d-flex">
        Catalog

        <button
          className="base-button icon-button icon-button-small evo-dark-green ml-2"
          title="Add Category"
          onClick={handleAddNewRow}
          disabled={state.isAddNewItemMode || !!state.selectedItem || !!state.selectedSubItem || state.isSaving}
        >
          <span className="material-icons">add_circle</span>
        </button>
      </h5>

      <div className="position-relative">
        {state.isLoading && <Loader additionalClasses="position-absolute top-50 start-50 translate-middle"/>}

        <TableContainer
          component={Paper}
          className="mt-0 shadow-none"
        >
          <Table>
            <TableHead className="fm-l2-parent-table-head dynamic-accordion-table-header">
              <TableRow className="fm-l2-parent-table-head-row">
                <TableCell className="fm-l2-parent-table-row-collapsing-actions">
                  <button
                    className="base-button icon-button icon-button-small btn-blue-inverted p-0"
                    title={`${state.expandedRows.length > 0 ? "Collapse" : "Expand"} All`}
                    onClick={handleToggleExpandAllRows}
                  >
                      <span className="material-icons">
                        {state.expandedRows.length > 0 ? "unfold_less" : "unfold_more"}
                      </span>
                  </button>
                </TableCell>
                <TableCell>
                  Categories / Subcategories
                </TableCell>
                <TableCell/>
              </TableRow>
            </TableHead>

            <TableBody className="fm-l2-parent-table-body">
              {state.isAddNewItemMode && !state.selectedItem && !state.selectedSubItem && (
                <TableRow className="fm-l2-parent-table-row">
                  <TableCell/>
                  <TableCell>
                    <TextInput
                      name="newCategoryName"
                      handleChange={(event) => {
                        dispatch({
                          type: "fieldChanged",
                          field: "newCategoryName",
                          value: event.target.value,
                        });
                      }}
                      value={state.newCategoryName}
                      className="w-100"
                    />
                  </TableCell>
                  <TableCell className="fm-l2-parent-table-row-actions">
                    <div className="d-flex justify-content-end">
                      <button
                        className="d-block base-button icon-button icon-button-small btn-green-inverted"
                        onClick={handleAddNewItem}
                        disabled={!state.newCategoryName || state.isSaving}
                        title="Save"
                      >
                        <span className="material-icons">check_circle</span>
                      </button>

                      <button
                        className="d-block base-button icon-button icon-button-small btn-danger-inverted"
                        onClick={handleHideNewRow}
                        disabled={state.isSaving}
                        title="Cancel"
                      >
                        <span className="material-icons">cancel</span>
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {state.data.length > 0 && state.data.map((row, index) => (
                <Fragment key={row._id}>
                  <TableRow
                    className="fm-l2-parent-table-row"
                  >
                    <TableCell>
                      {row.subCategories?.length > 0 && (
                        <button
                          className="base-button icon-button icon-button-small btn-blue-inverted"
                          title={`${state.expandedRows.includes(row._id) ? "Expand" : "Collapse"} Row`}
                          onClick={() => handleToggleExpandRow(row._id)}
                        >
                          <span className="material-icons">
                            {state.expandedRows.includes(row._id) ? "expand_less" : "expand_more"}
                          </span>
                        </button>
                      )}
                    </TableCell>
                    <TableCell>
                      {!state.isAddNewItemMode && !state.selectedSubItem && !state.isDeleteModalOpen && state.selectedItem?._id === row._id ? (
                        <TextInput
                          name="selectedItemName"
                          handleChange={(event) => {
                            dispatch({
                              type: "fieldChanged",
                              field: "selectedItem.name",
                              value: event.target.value,
                            });
                          }}
                          value={state.selectedItem?.name || ""}
                          className="w-100"
                        />
                      ) : (
                        row.name
                      )}
                    </TableCell>

                    <TableCell className="fm-l2-parent-table-row-actions">
                      {!state.isAddNewItemMode && !state.selectedItem && !state.selectedSubItem && (
                        <div className="justify-content-end">
                          {index !== 0 && (
                            <button
                              className="d-block base-button icon-button icon-button-small btn-blue-inverted"
                              onClick={() => handleMoveItemUp(row)}
                              disabled={state.isSaving}
                              title="Move Category Up"
                            >
                              <span className="material-icons">arrow_upward</span>
                            </button>
                          )}

                          {index !== state.data.length - 1 && (
                            <button
                              className="d-block base-button icon-button icon-button-small btn-blue-inverted"
                              onClick={() => handleMoveItemDown(row)}
                              disabled={state.isSaving}
                              title="Move Category Down"
                            >
                              <span className="material-icons">arrow_downward</span>
                            </button>
                          )}

                          <button
                            className="d-block base-button icon-button icon-button-small btn-green-inverted"
                            onClick={() => handleAddSubItem(row)}
                            disabled={state.isSaving}
                            title="Add Subcategory"
                          >
                            <span className="material-icons">add_circle</span>
                          </button>

                          <button
                            className="d-block base-button icon-button icon-button-small btn-blue-inverted"
                            onClick={() => handleEditItem(row)}
                            disabled={state.isSaving}
                            title="Edit Category Name"
                          >
                            <span className="material-icons">edit</span>
                          </button>

                          <button
                            className="d-block base-button icon-button icon-button-small btn-danger-inverted"
                            onClick={() => handleDeleteItem(row)}
                            disabled={state.isSaving}
                            title="Delete Category"
                          >
                            <span className="material-icons">delete</span>
                          </button>
                        </div>
                      )}
                      {!state.isAddNewItemMode && !state.selectedSubItem && !state.selectedSubItem && !state.isDeleteModalOpen && state.selectedItem?._id === row._id && (
                        <div className="d-flex justify-content-end">
                          <button
                            className="d-block base-button icon-button icon-button-small btn-green-inverted"
                            onClick={handleEditItemConfirm}
                            disabled={!state.selectedItem?.name || state.isSaving}
                            title="Save"
                          >
                            <span className="material-icons">check_circle</span>
                          </button>

                          <button
                            className="d-block base-button icon-button icon-button-small btn-danger-inverted"
                            onClick={handleCancelEditItem}
                            disabled={state.isSaving}
                            title="Cancel"
                          >
                            <span className="material-icons">cancel</span>
                          </button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  {state.isAddNewItemMode && state.selectedItem?._id === row._id && (
                    <TableRow className="fm-l2-parent-table-row fm-l2-parent-table-row-children">
                      <TableCell/>
                      <TableCell>
                        <TextInput
                          name="newSubCategoryName"
                          handleChange={(event) => {
                            dispatch({
                              type: "fieldChanged",
                              field: "newSubCategoryName",
                              value: event.target.value,
                            });
                          }}
                          value={state.newSubCategoryName}
                          className="w-100"
                        />
                      </TableCell>
                      <TableCell className="fm-l2-parent-table-row-actions">
                        <div className="d-flex justify-content-end">
                          <button
                            className="d-block base-button icon-button icon-button-small btn-green-inverted"
                            onClick={handleAddNewSubItem}
                            disabled={!state.newSubCategoryName || state.isSaving}
                            title="Save"
                          >
                            <span className="material-icons">check_circle</span>
                          </button>

                          <button
                            className="d-block base-button icon-button icon-button-small btn-danger-inverted"
                            onClick={handleHideNewSubRow}
                            disabled={state.isSaving}
                            title="Cancel"
                          >
                            <span className="material-icons">cancel</span>
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {state.expandedRows.includes(row._id) && row.subCategories?.map((subCategory, index) => (
                    <TableRow
                      key={subCategory._id}
                      className="fm-l2-parent-table-row fm-l2-parent-table-row-children"
                    >
                      <TableCell/>
                      <TableCell>
                        {!state.isAddNewItemMode && !state.isDeleteModalOpen && state.selectedSubItem?._id === subCategory._id ? (
                          <TextInput
                            name="selectedSubItemName"
                            handleChange={(event) => {
                              dispatch({
                                type: "fieldChanged",
                                field: "selectedSubItem.name",
                                value: event.target.value,
                              });
                            }}
                            value={state.selectedSubItem?.name || ""}
                            className="w-100"
                          />
                        ) : (
                          subCategory.name
                        )}
                      </TableCell>

                      <TableCell className="fm-l2-parent-table-row-actions">
                        {!state.isAddNewItemMode && !state.selectedItem && !state.selectedSubItem && (
                          <div className="justify-content-end">
                            {index !== 0 && (
                              <button
                                className="d-block base-button icon-button icon-button-small btn-blue-inverted"
                                onClick={() => handleMoveSubItemUp(subCategory)}
                                disabled={state.isSaving}
                                title="Move Subcategory Up"
                              >
                                <span className="material-icons">arrow_upward</span>
                              </button>
                            )}

                            {index !== row.subCategories.length - 1 && (
                              <button
                                className="d-block base-button icon-button icon-button-small btn-blue-inverted"
                                onClick={() => handleMoveSubItemDown(subCategory)}
                                disabled={state.isSaving}
                                title="Move Subcategory Down"
                              >
                                <span className="material-icons">arrow_downward</span>
                              </button>
                            )}

                            <button
                              className="d-block base-button icon-button icon-button-small btn-blue-inverted"
                              onClick={() => handleEditSubItem(subCategory)}
                              disabled={state.isSaving}
                              title="Edit Subcategory Name"
                            >
                              <span className="material-icons">edit</span>
                            </button>

                            <button
                              className="d-block base-button icon-button icon-button-small btn-danger-inverted"
                              onClick={() => handleDeleteSubItem(subCategory)}
                              disabled={state.isSaving}
                              title="Delete Subcategory"
                            >
                              <span className="material-icons">delete</span>
                            </button>
                          </div>
                        )}
                        {!state.isAddNewItemMode && !state.isDeleteModalOpen && state.selectedSubItem?._id === subCategory._id && (
                          <div className="d-flex justify-content-end">
                            <button
                              className="d-block base-button icon-button icon-button-small btn-green-inverted"
                              onClick={handleEditSubItemConfirm}
                              disabled={!state.selectedSubItem?.name || state.isSaving}
                              title="Save"
                            >
                              <span className="material-icons">check_circle</span>
                            </button>

                            <button
                              className="d-block base-button icon-button icon-button-small btn-danger-inverted"
                              onClick={handleCancelEditSubItem}
                              disabled={state.isSaving}
                              title="Cancel"
                            >
                              <span className="material-icons">cancel</span>
                            </button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}

              {!state.data.length && !state.isAddNewItemMode && (
                <TableRow
                  className="fm-l2-parent-table-row"
                >
                  <TableCell colSpan={3}>
                    There are no records to display
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <ReactModal
        isOpen={state.isDeleteModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseDeleteModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete {state.selectedSubItem ? "Subcategory" : "Category"}</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            {
              state.selectedSubItem ?
                "Are you sure you want to delete this subcategory? This action cannot be undone." :
                "Are you sure you want to delete this category? This action cannot be undone. All subcategories under this category will be deleted as well."
            }
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseDeleteModal}
              disabled={state.isSaving}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              onClick={state.selectedSubItem ? handleDeleteSubConfirm : handleDeleteConfirm}
              isLoading={state.isSaving}
            >
              Delete {state.selectedSubItem ? "Subcategory" : "Category"}
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default CateringCatalog;
